import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import MediaItem from "@/components/MediaItem";
import logoImage from "@assets/legacyloop-white-trans.png";
import { BookText, ImageIcon, Lock, Loader2 } from "lucide-react";

interface LoopData {
  member: {
    name: string;
    relationship: string | null;
    accessLevel: string;
  };
  athlete: {
    id: string;
    name: string;
    sports: string;
    position: string | null;
    school: string | null;
    graduationYear: number | null;
    bio: string | null;
  };
  media: any[];
  journal: any[];
}

const LoopView = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<LoopData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/loop/${token}`)
      .then((res) => {
        if (res.status === 404) { setNotFound(true); return null; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => { if (json) setData(json); })
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#060d18] via-[#0D1B2A] to-[#1B263B] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 text-[#E0A458] animate-spin" />
          <p className="text-slate-400 text-sm">Loading the Loop...</p>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#060d18] via-[#0D1B2A] to-[#1B263B] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <Lock className="h-12 w-12 text-[#E0A458]/50 mx-auto mb-4" />
          <h2 className="font-montserrat font-bold text-2xl text-white mb-2">Loop not found</h2>
          <p className="text-slate-400 text-sm">
            This link may have expired or been removed. Contact the family to request a new invite.
          </p>
        </div>
      </div>
    );
  }

  const { member, athlete, media, journal } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#060d18] via-[#0D1B2A] to-[#1B263B] text-white">
      {/* Minimal header */}
      <header className="bg-[#0D1B2A]/90 backdrop-blur-sm border-b border-[#1B263B] sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
        <img src={logoImage} alt="LegacyLoop" className="h-8 w-auto" />
        <span className="text-xs text-slate-400 bg-[#1B263B] px-3 py-1 rounded-full border border-[#415A77]/40">
          Shared with {member.name}
        </span>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Athlete hero card */}
        <Card className="mb-6 overflow-hidden border border-[#E0A458]/20 bg-[#0D1B2A]">
          <div className="h-28 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] border-b border-[#E0A458]/20" />
          <div className="px-6 pt-4 pb-6">
            <h1 className="font-montserrat font-bold text-2xl text-white">{athlete.name}</h1>
            <p className="text-[#E0A458]/90 font-medium mt-0.5">
              {athlete.sports}{athlete.position ? ` · ${athlete.position}` : ""}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {athlete.graduationYear && (
                <Badge className="bg-[#E0A458]/15 text-[#E0A458] border border-[#E0A458]/40 px-3 py-1">
                  Class of {athlete.graduationYear}
                </Badge>
              )}
              {athlete.school && (
                <Badge className="bg-[#1B263B] text-slate-200 border border-[#415A77] px-3 py-1">
                  {athlete.school}
                </Badge>
              )}
              {member.relationship && (
                <Badge className="bg-[#1B263B] text-slate-300 border border-[#415A77] px-3 py-1">
                  Shared with {member.relationship}
                </Badge>
              )}
            </div>
            {athlete.bio && (
              <p className="mt-4 text-slate-300 text-sm leading-relaxed">{athlete.bio}</p>
            )}
          </div>
        </Card>

        {/* Media section */}
        <section className="mb-8">
          <h2 className="font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-4">
            Highlights
          </h2>
          {media.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((item: any) => (
                <MediaItem
                  key={item.id}
                  item={item}
                  thumbnailHeight="h-48"
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 flex flex-col items-center gap-3 py-12 text-center">
              <ImageIcon className="h-8 w-8 text-[#E0A458]/50" />
              <p className="text-slate-300 font-medium">No highlights yet.</p>
              <p className="text-sm text-slate-400">Check back soon.</p>
            </div>
          )}
        </section>

        {/* Journal section — viewer access only */}
        {member.accessLevel !== "limited" && (
          <section className="mb-8">
            <h2 className="font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-4">
              Journal
            </h2>
            {journal.length > 0 ? (
              <div className="space-y-4">
                {journal.map((entry: any) => (
                  <Card key={entry.id} className="border border-[#415A77]/40 bg-[#0D1B2A]">
                    <CardContent className="p-4">
                      <p className="font-montserrat font-semibold text-white mb-1">{entry.title}</p>
                      <p className="text-xs text-slate-400 mb-3">
                        {new Date(entry.date || entry.createdAt).toLocaleDateString("en-US", {
                          year: "numeric", month: "long", day: "numeric",
                        })}
                      </p>
                      <p className="text-slate-300 text-sm leading-relaxed line-clamp-4">{entry.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 flex flex-col items-center gap-3 py-10 text-center">
                <BookText className="h-8 w-8 text-[#E0A458]/50" />
                <p className="text-slate-300 font-medium">No journal entries yet.</p>
              </div>
            )}
          </section>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-8 border-t border-[#1B263B]">
          <img src={logoImage} alt="LegacyLoop" className="h-10 w-auto mx-auto mb-2 opacity-60" />
          <p className="text-xs text-slate-500">
            Powered by LegacyLoop &mdash; Preserve the Journey. Relive Every Moment.
          </p>
          <Button
            variant="link"
            className="text-[#E0A458] text-xs mt-1"
            onClick={() => window.open("https://mylegacyloop.com", "_blank")}
          >
            Create your own Loop
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoopView;
